import { useCallback, useEffect, useState } from 'react';

/**
 * UI preferences — the settings that follow the user rather than the screen.
 *
 * The companion to `useScreenState`, and the distinction between them is worth
 * getting right:
 *
 *  - **In the URL** (`useScreenState`): anything that changes what the screen
 *    shows. It is per-screen, it is shareable, and it is what a link means.
 *  - **Here**: chrome the user has arranged to their taste — sidebar collapsed,
 *    which nav groups are open, table density. It is the same on every screen,
 *    it is personal to this machine, and nobody wants it in a pasted link.
 *
 * Both survive a reload; only the first survives being sent to a colleague.
 *
 * Storage is best-effort. A locked-down browser profile that throws on
 * `localStorage` should cost the user their sidebar width, not the app.
 */

const PREFIX = 'ui.';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    // Absent, unparseable, or storage denied — all mean "use the default".
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Private mode or a full quota. Losing a preference is not worth an error.
  }
}

/**
 * `useState` that remembers.
 *
 * ```ts
 * const [collapsed, setCollapsed] = usePersistentState('sidebar.collapsed', false);
 * ```
 *
 * The initial read is lazy, so it happens once on mount rather than on every
 * render, and the write happens in an effect after commit — the user's click
 * must not wait on a synchronous disk-backed write.
 */
export function usePersistentState<T>(key: string, fallback: T) {
  const [value, setValue] = useState<T>(() => read(key, fallback));

  useEffect(() => {
    write(key, value);
  }, [key, value]);

  return [value, setValue] as const;
}

/**
 * The same thing for a set of ids — which sidebar groups are expanded, which
 * table rows are unfolded. Stored as an array because a `Set` is not JSON.
 */
export function usePersistentSet(key: string, fallback: string[]) {
  const [value, setValue] = useState<Set<string>>(() => new Set(read(key, fallback)));

  useEffect(() => {
    write(key, [...value]);
  }, [key, value]);

  const toggle = useCallback((id: string) => {
    setValue((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return [value, toggle] as const;
}
